"use client";

import type { AuthError, Session } from "@supabase/supabase-js";
import { usePostHog } from "posthog-js/react";
import { useEffect, useRef, useState } from "react";
import Button from "./Button";
import InfoTile from "./InfoTile";
import { useSupabaseClient } from "./SupabaseProvider";
import { TURNSTILE_FAILED_MESSAGE, useTurnstile } from "./TurnstileGate";

// Supabase's "Email OTP Length" is configurable from 6 to 10 digits.
const MIN_CODE_LENGTH = 6;
const MAX_CODE_LENGTH = 10;
// Supabase's default per-address "Email max frequency".
const RESEND_COOLDOWN_SECONDS = 60;

const RATE_LIMIT_SECONDS = /after (\d+) seconds?/;

const messageForError = (
  error: AuthError | null,
  kind: "signup" | "magic_link",
): string => {
  switch (error?.code) {
    // Also what a wrong code, or one replaced by a resend, returns. And
    // a signup code dies once the email's link has been opened (another
    // device, a mail scanner): the account is confirmed by then.
    case "otp_expired":
      return kind === "signup"
        ? "That code is wrong or has expired. Check the latest email, or send a new code. Already opened the link in the email? You're confirmed, so sign in instead."
        : "That code is wrong or has expired. Check the latest email, or send a new code.";
    case "over_request_rate_limit":
      return "Too many attempts. Wait a few minutes and try again.";
    case "over_email_send_rate_limit":
      return "We can't send another email right now. Try again in a little while.";
    case "captcha_failed":
      return TURNSTILE_FAILED_MESSAGE;
    default:
      return error?.message ?? "Something went wrong. Please try again.";
  }
};

type EmailCodeFormProps = {
  email: string;
  // What sent the email: a password signup ("Confirm signup" template) or a
  // magic link request (either template, depending on whether the address
  // is already confirmed). One verify call covers both.
  kind: "signup" | "magic_link";
  // Where the link in a re-sent email lands.
  redirectTo?: string;
  submitLabel?: string;
  // Runs right before verifyOtp: the session it creates reaches
  // onAuthStateChange subscribers before the call even resolves.
  onBeforeVerify?: () => void;
  onVerifyFailed?: () => void;
  onVerified?: (session: Session) => void;
};

// The code from the signup confirmation / magic link email, typed in place,
// so signing up never has to leave the page (the email's link still works
// too). Needs `{{ .Token }}` in Supabase's "Confirm signup" and "Magic Link"
// email templates.
export default function EmailCodeForm({
  email,
  kind,
  redirectTo,
  submitLabel = "Verify",
  onBeforeVerify,
  onVerifyFailed,
  onVerified,
}: EmailCodeFormProps) {
  const supabase = useSupabaseClient();
  const posthog = usePostHog();
  const inputRef = useRef<HTMLInputElement>(null);
  // Only for Resend (/verify isn't captcha-protected). Mounted on the first
  // Resend click rather than up front: getToken() waits for the widget.
  const turnstile = useTurnstile();
  const [captchaActive, setCaptchaActive] = useState(false);

  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  // The email just went out, so the same cooldown applies already.
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_SECONDS);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((left) => left - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const verify = async (token: string) => {
    if (verifying) return;
    setVerifying(true);
    setError(null);
    onBeforeVerify?.();
    try {
      const { data, error } = await supabase.auth.verifyOtp({
        email,
        token,
        type: "email",
      });
      if (error || !data.session) {
        posthog?.capture("Email Code Verification Failed", {
          kind,
          error_code: error?.code,
        });
        onVerifyFailed?.();
        setError(messageForError(error, kind));
        setVerifying(false);
        refocusInput();
        return;
      }
      posthog?.capture("Email Code Verified", { kind });
      // Left spinning: the new session takes it from here.
      onVerified?.(data.session);
    } catch {
      onVerifyFailed?.();
      setError(messageForError(null, kind));
      setVerifying(false);
      refocusInput();
    }
  };

  // Back to the field (verify / resend took focus off it, which in the
  // signup dialog would also stop Escape from working), selected so the
  // next paste replaces the wrong code.
  const refocusInput = () => {
    inputRef.current?.focus();
    inputRef.current?.select();
  };

  const resend = async () => {
    setResending(true);
    setResent(false);
    setError(null);
    setCaptchaActive(true);
    try {
      const captchaToken = await turnstile.getToken();
      const { error } =
        kind === "signup"
          ? await supabase.auth.resend({
              type: "signup",
              email,
              options: { emailRedirectTo: redirectTo, captchaToken },
            })
          : // There's no magic link resend: asking again sends a new one.
            await supabase.auth.signInWithOtp({
              email,
              options: { emailRedirectTo: redirectTo, captchaToken },
            });
      if (error) {
        const wait = error.message.match(RATE_LIMIT_SECONDS);
        if (wait) setCooldown(Number(wait[1]));
        else setError(messageForError(error, kind));
        return;
      }
      posthog?.capture("Email Code Resent", { kind });
      setCode("");
      setResent(true);
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch {
      setError(TURNSTILE_FAILED_MESSAGE);
    } finally {
      setResending(false);
      turnstile.reset();
      refocusInput();
    }
  };

  return (
    <form
      className="w-full space-y-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (code.length >= MIN_CODE_LENGTH) void verify(code);
      }}
    >
      <input
        ref={inputRef}
        aria-label="Confirmation code"
        aria-invalid={!!error || undefined}
        aria-busy={verifying || undefined}
        autoFocus
        autoComplete="one-time-code"
        inputMode="numeric"
        placeholder="Enter code"
        value={code}
        // Not `disabled`: that would drop focus out of the field (and the
        // signup dialog) while verifying.
        readOnly={verifying}
        onChange={(event) => {
          // No maxLength: it would cut a paste like "Your code: 123456"
          // before the digits are picked out of it.
          const digits = event.target.value
            .replace(/\D/g, "")
            .slice(0, MAX_CODE_LENGTH);
          const { inputType } = event.nativeEvent as InputEvent;
          const inserted =
            inputType === "insertFromPaste" ||
            inputType === "insertReplacementText" ||
            inputType === undefined ||
            digits.length - code.length > 1;
          setCode(digits);
          setError(null);
          // Pasted or autofilled whole: go. Typed digit by digit, wait for
          // the button, since the code may be longer than 6 digits.
          if (inserted && digits.length >= MIN_CODE_LENGTH) {
            void verify(digits);
          }
        }}
        className="w-full px-4 py-3 text-center text-2xl font-medium tracking-[0.4em] text-slate-900 border border-slate-300 rounded-xl font-sans placeholder:text-base placeholder:font-normal placeholder:tracking-normal placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-transparent read-only:opacity-60"
      />
      <Button
        type="submit"
        size="l"
        className="w-full"
        loading={verifying}
        disabled={code.length < MIN_CODE_LENGTH}
      >
        {submitLabel}
      </Button>
      <div role="status" aria-live="polite" className="empty:hidden">
        {error && <InfoTile size="s" description={error} />}
        {resent && !error && (
          <InfoTile
            size="s"
            description={
              // Resending to an already-confirmed address is a silent
              // no-op on Supabase's side, so don't promise an email.
              kind === "signup"
                ? "If this email still needs confirming, a new code is on its way. Codes from earlier emails no longer work."
                : "New code sent. Codes from earlier emails no longer work."
            }
          />
        )}
      </div>
      {captchaActive && turnstile.widget}
      <p className="text-center text-sm text-slate-500 font-sans">
        Didn&apos;t get it?{" "}
        <button
          type="button"
          onClick={resend}
          disabled={cooldown > 0 || resending}
          className="font-medium text-slate-700 underline underline-offset-2 disabled:no-underline disabled:text-slate-400 disabled:cursor-default"
        >
          {resending
            ? "Sending…"
            : cooldown > 0
              ? `Resend code in ${cooldown}s`
              : "Resend code"}
        </button>
      </p>
    </form>
  );
}
